import { useTranslationsProvider } from '@mfw/components';
import React from 'react';

import { accountLocale } from '../utils/accountLocale';
import { useTenantContext } from './TenantProvider';
import { useAuthContext } from './AuthProvider';

export type UserLocaleSyncProps = {
  /**
   * Whether the ACCOUNT decides the language on this route.
   *
   * `false` on the anonymous public page (`publicSiteRoutes.tsx`), where the
   * language is the page's own (`PublicPages.locale`) — the owner's choice of
   * what a shared link says, not a property of whoever opens it. Both writers
   * target the same app-wide `TranslationsProvider`, so with a signed-in
   * visitor whose account language differs, each effect undid the other's
   * write: ~900 locale switches in five seconds, the page flipping between
   * its spinner and its content, and the scroll position snapping back to the
   * top on every flip.
   */
  isEnabled: boolean;
};

/**
 * Applies the user's saved language (userSettings.locale) to the app-wide
 * TranslationsProvider on every boot. Historically this only happened inside
 * LocaleSelector (login/onboarding/settings screens), so a direct visit to any
 * other page silently fell back to the default language. An account with no
 * language of its own takes the tenant's, and so does a signed-out visitor at the
 * start.
 */
const UserLocaleSync = ({ isEnabled }: UserLocaleSyncProps) => {
  const { currentUser, isLoading } = useAuthContext();
  const { tenant } = useTenantContext();
  const { locale, updateLocaleAsync } = useTranslationsProvider();

  const savedLocale =
    isEnabled && currentUser
      ? accountLocale(currentUser.settings, tenant?.defaultLocale)
      : undefined;
  React.useEffect(() => {
    if (savedLocale && savedLocale !== locale) {
      updateLocaleAsync(savedLocale as Parameters<typeof updateLocaleAsync>[0]);
    }
  }, [savedLocale, locale, updateLocaleAsync]);

  // Decided once: run again, it would undo what the visitor picks on the sign-in
  // screen. It waits for auth, or a restored session flashes the tenant's language.
  const isVisitorLocaleDecided = React.useRef(false);
  React.useEffect(() => {
    if (!isEnabled || isLoading || !tenant || isVisitorLocaleDecided.current) {
      return;
    }
    isVisitorLocaleDecided.current = true;
    if (!currentUser && tenant.defaultLocale) {
      updateLocaleAsync(
        tenant.defaultLocale as Parameters<typeof updateLocaleAsync>[0],
      );
    }
  }, [currentUser, isEnabled, isLoading, tenant, updateLocaleAsync]);

  return null;
};

export default UserLocaleSync;
